import { useEffect, useState } from "react";
import { FileText } from "lucide-react";

import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/admin/_common/ui/select";
import type { Automation, AutomationSchema } from "@/app/_services/AutomationService";

import EmailTemplateBuilder from "./EmailTemplateBuilder";
import SmsTemplateEditor from "./SmsTemplateEditor";

/**
 * Picks the editing surface from the template's channel: the block builder for email,
 * a plain message box for SMS. This tab runs full-bleed, so each surface owns its own
 * scrolling and width.
 */
export default function TemplateTab({
  automation,
  schema,
  onSave,
}: {
  automation: Automation;
  schema: AutomationSchema | null;
  onSave: (body: {
    key: string;
    subject?: string;
    body?: string;
    body_json?: any;
  }) => Promise<boolean>;
}) {
  const templates = automation.templates ?? [];
  const [activeKey, setActiveKey] = useState(templates[0]?.key ?? "");

  useEffect(() => {
    if (!templates.find((t) => t.key === activeKey)) {
      setActiveKey(templates[0]?.key ?? "");
    }
  }, [automation.id, automation.updated_at]);

  const template = templates.find((t) => t.key === activeKey);

  // The event payload fields are deliberately not used here -- handlers re-fetch, so no
  // payload name survives into the context a template renders against.
  const contextFields =
    schema?.events.find((e) => e.key === automation.event_key)?.context_fields ?? [];
  const legacyFields = schema?.legacy_flat_fields ?? [];

  if (!template) {
    return (
      <div className="flex min-h-[320px] flex-1 flex-col">
        <CuoEmpty icon={FileText} text="This automation has no template" />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {templates.length > 1 && (
        <div className="flex flex-shrink-0 items-center gap-2 border-b border-border bg-card px-5 py-2">
          <span className="text-[12.5px] text-muted-foreground">Template</span>
          <Select value={activeKey} onValueChange={setActiveKey}>
            <SelectTrigger className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {templates.map((t) => (
                <SelectItem key={t.key} value={t.key}>
                  {t.key} ({t.channel})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {template.channel === "sms" ? (
        <SmsTemplateEditor
          // Remount on save so the draft resets against the refetched body.
          key={`${template.key}:${automation.updated_at}`}
          template={template}
          contextFields={contextFields}
          onSave={onSave}
        />
      ) : (
        <EmailTemplateBuilder
          automationId={automation.id}
          template={template}
          contextFields={contextFields}
          legacyFields={legacyFields}
          onSave={onSave}
        />
      )}
    </div>
  );
}
